import {
  CloseCircleOutlined,
  FilterOutlined,
  PlusCircleOutlined,
  SlidersOutlined,
  SearchOutlined,
} from '@ant-design/icons'
import {
  Button,
  Col,
  Empty,
  Input,
  message,
  Pagination,
  Row,
  Space,
  Popover,
  Select,
  InputNumber,
} from 'antd'
import debounce from 'lodash/debounce'
import { inject, observer } from 'mobx-react'
import queryString from 'query-string'
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Helmet } from 'react-helmet'
import { useLocation, withRouter } from 'react-router-dom'

import AccessControlAction from '../../../components/AccessControlAction'
import { AvatarHasName } from '../../../components/Common/Avatar'
import { trimOverlengthString } from '../../../components/Common/CellText'
import { StatusTag } from '../../../components/Common/StatusTag'
import { TableBottomPaginationBlock } from '../../../components/Common/Table'
import TableComponent from '../../../components/Common/TableComponent'
import PageTitle from '../../../components/PageTitle'
import AppPageHeader from '../../../components/AppPageHeader'
import { systemName } from '../../../config'
import {
  ACL_ACTION_TYPE,
  ASSIGNEE_TYPE,
  DATE_FORMAT_DEFAULT,
  TYPE_STATUS,
} from '../../../constants'
import dayjs from '../../../dayjsConfig'
import DocumentsLayout from '../../../layouts/DocumentsLayout'
import { SearchBar } from '../InternalDocumentOutgoingDocPage/InternalDocumentOutgoingDocPageStyled'
import AdvanceFilterBlock from './AdvanceFilterBlock'
import { FilterButtonWrapper } from './InternalDocumentSignDocPageStyled'
import PopupCreateSignDoc from './PopupCreateSignDoc'

const { Search } = Input

const InternalDocumentSignDocPage = props => {
  const { history, electronicDocumentStore, loadingAnimationStore } = props
  const {
    electronicDocumentList,
    totalCount,
    queryParamsSignDocument,
  } = electronicDocumentStore

  const location = useLocation()
  const queryStringParse = queryString.parse(location.search)

  const [searchValue, setSearchValue] = useState(queryStringParse.title || '')
  const [showAdvanceFilter, setShowAdvanceFilter] = useState(false)
  const [
    isVisiblePopupCreateSignDoc,
    setIsVisiblePopupCreateSignDoc,
  ] = useState(false)
  const [countFilter, setCountFilter] = useState(0)
  const [isRefreshPage, setIsRefreshPage] = useState(true)
  const [checkEmpty, setCheckEmpty] = useState(true)

  // Use ref to prevent infinite loop
  const isInitialMount = useRef(true)
  const STORAGE_KEY = 'sign_document_pagination_state'

  const tableColumns = useMemo(
    () => [
      {
        title: 'Tiêu đề',
        render: record => trimOverlengthString(record.title, 450),
      },
      {
        title: 'Người trình ký',
        width: 180,
        render: record => {
          const creator = record.assignees.find(
            user => user.permission === ASSIGNEE_TYPE.CREATE
          )
          return (
            <AvatarHasName
              imgId={creator.image}
              name={creator.name_uppercase}
            />
          )
        },
      },
      {
        align: 'center',
        title: 'Trạng thái',
        width: 100,
        render: record => (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            {StatusTag(TYPE_STATUS.ELECTRONIC_DOCUMENT, record.status)}
          </div>
        ),
      },
      {
        align: 'center',
        title: 'Ngày trình',
        width: 120,
        render: record => (
          <span style={{ whiteSpace: 'nowrap' }}>
            {dayjs(record.created_date).format(DATE_FORMAT_DEFAULT)}
          </span>
        ),
      },
    ],
    []
  )

  const getElectronicDocumentList = useCallback(async () => {
    try {
      loadingAnimationStore.setTableLoading(true)
      setCheckEmpty(false)
      await electronicDocumentStore.getElectronicDocumentList()
    } catch (error) {
      console.error(error)
      message.error(error?.vi || 'Đã có lỗi xảy ra!')
    } finally {
      loadingAnimationStore.setTableLoading(false)
      setCheckEmpty(true)
    }
  }, [electronicDocumentStore, loadingAnimationStore])

  // Initialize từ URL hoặc sessionStorage chỉ 1 lần
  useEffect(() => {
    // Lấy trạng thái đã lưu từ sessionStorage
    const savedState = sessionStorage.getItem(STORAGE_KEY)

    if (savedState && !Object.keys(queryStringParse).length) {
      // Nếu có trạng thái đã lưu và URL không có params, restore từ storage
      const parsedState = JSON.parse(savedState)

      // Cập nhật URL với trạng thái đã lưu
      const queryStringStringify = queryString.stringify(parsedState, {
        skipNull: true,
      })
      history.replace(
        `/internal-document/sign-document?${queryStringStringify}`
      )

      // Cập nhật store
      electronicDocumentStore.setQueryParamsSignDocument({
        page: parsedState.page || 0,
        size: parsedState.size || 10,
        title: parsedState.title || null,
        status: parsedState.status || null,
        from_date: parsedState.from_date || null,
        to_date: parsedState.to_date || null,
      })
    } else if (Object.keys(queryStringParse).length > 0) {
      // Nếu URL có params, đồng bộ với store
      electronicDocumentStore.setQueryParamsSignDocument({
        page: queryStringParse.page ? parseInt(queryStringParse.page) : 0,
        size: queryStringParse.size ? parseInt(queryStringParse.size) : 10,
        title: queryStringParse.title || null,
        status: queryStringParse.status || null,
        from_date: queryStringParse.from_date || null,
        to_date: queryStringParse.to_date || null,
      })

      // Lưu trạng thái hiện tại vào sessionStorage
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(queryStringParse))
    } else {
      // Lần đầu vào trang, clear all
      electronicDocumentStore.clearQueryParamsIncoming()
      sessionStorage.removeItem(STORAGE_KEY)
    }

    setIsRefreshPage(false)

    return () => {
      electronicDocumentStore.clearFilter()
      electronicDocumentStore.clearElectronicDocumentList()
    }
  }, [])

  // Fetch data khi queryParams thay đổi, nhưng skip lần đầu
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }

    if (isRefreshPage) return

    getElectronicDocumentList()
  }, [
    queryParamsSignDocument.page,
    queryParamsSignDocument.size,
    queryParamsSignDocument.title,
    queryParamsSignDocument.status,
    queryParamsSignDocument.from_date,
    queryParamsSignDocument.to_date,
    isRefreshPage,
    getElectronicDocumentList,
  ])

  // Cập nhật số lượng filter
  useEffect(() => {
    let count = 0
    if (queryStringParse.title) count++
    if (queryStringParse.status) count++
    if (queryStringParse.from_date && queryStringParse.to_date) count++
    setCountFilter(count)
  }, [
    queryStringParse.title,
    queryStringParse.status,
    queryStringParse.from_date,
    queryStringParse.to_date,
  ])

  // Đồng bộ search value từ URL
  useEffect(() => {
    setSearchValue(queryStringParse.title || '')
  }, [queryStringParse.title])

  const onChangePagination = useCallback(
    pageIndex => {
      const newParams = {
        ...queryStringParse,
        page: pageIndex - 1,
        size: 10,
      }

      electronicDocumentStore.setQueryParamsSignDocument({
        page: pageIndex - 1,
        size: 10,
        title: queryStringParse.title || null,
        status: queryStringParse.status || null,
        from_date: queryStringParse.from_date || null,
        to_date: queryStringParse.to_date || null,
      })

      const queryStringStringify = queryString.stringify(newParams, {
        skipNull: true,
      })
      history.replace(
        `/internal-document/sign-document?${queryStringStringify}`
      )

      // Lưu trạng thái mới vào sessionStorage
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(newParams))
    },
    [electronicDocumentStore, queryStringParse, history]
  )

  // Tạo debounced function 1 lần duy nhất
  const debouncedSearch = useMemo(
    () =>
      debounce((title, currentQueryParams, store, historyObj) => {
        const newParams = {
          ...currentQueryParams,
          page: 0,
          size: 10,
          title: title ? title.trim() : null,
        }

        store.setQueryParamsSignDocument({
          page: 0,
          size: 10,
          title: title && title.trim(),
          status: currentQueryParams.status || null,
          from_date: currentQueryParams.from_date || null,
          to_date: currentQueryParams.to_date || null,
        })

        const queryStringStringify = queryString.stringify(newParams, {
          skipNull: true,
        })
        historyObj.replace(
          `/internal-document/sign-document?${queryStringStringify}`
        )
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(newParams))
      }, 500),
    []
  )

  const handleQuickSearch = useCallback(
    title => {
      debouncedSearch(title, queryStringParse, electronicDocumentStore, history)
    },
    [debouncedSearch, queryStringParse, electronicDocumentStore, history]
  )

  const handleRowClick = useCallback(
    record => {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(queryStringParse))
      history.push(`/internal-document/sign-document/view/${record.code}`, {
        from: location.pathname + location.search,
      })
    },
    [queryStringParse, history, location.pathname, location.search]
  )

  return (
    <DocumentsLayout
      title={
        <AppPageHeader
          title="Phát hành văn bản điện tử"
          addon={
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Search
                style={{ width: 300 }}
                size="large"
                allowClear
                value={searchValue}
                onChange={e => setSearchValue(e.target.value)}
                onSearch={handleQuickSearch}
                placeholder="Tìm kiếm trình ký theo tiêu đề..."
                className="custom-search-input fixed-width"
              />
              <FilterButtonWrapper style={{ display: 'inline-block' }}>
                <span
                  className={'count-filter'}
                  style={{ display: !countFilter && 'none' }}>
                  {countFilter}
                </span>
                <Popover
                  content={<AdvanceFilterBlock onClose={() => setShowAdvanceFilter(false)} />}
                  trigger="click"
                  visible={showAdvanceFilter}
                  onVisibleChange={visible => setShowAdvanceFilter(visible)}
                  placement="bottomRight"
                  overlayClassName="custom-page-filter-dropdown-container">
                  <Button
                    size="large"
                    icon={<SlidersOutlined />}
                  />
                </Popover>
              </FilterButtonWrapper>

              <AccessControlAction aclActionType={ACL_ACTION_TYPE.create__PHAT_HANH_VB}>
                <Button
                  size="large"
                  icon={<PlusCircleOutlined />}
                  onClick={() => setIsVisiblePopupCreateSignDoc(true)}
                  type={'primary'}>
                  Tạo trình ký
                </Button>
              </AccessControlAction>
            </div>
          }
        />
      }
      selectedKey="sign-document"
      linkToIncomingDocument="/internal-document/incoming-document"
      linkToOutgoingDocument="/internal-document/outgoing-document"
      linkToSignDocument="/internal-document/sign-document"
      linkToConfigDocument="/internal-document/config-document/document-books">
      <Helmet>
        <title>
          Phát hành văn bản điện tử | {systemName} Quản lý VB {systemName}
        </title>
      </Helmet>

      <div>
        <TableComponent
          onRow={record => ({
            onClick: () => handleRowClick(record),
          })}
          rowKey={record => record.code}
          dataSource={electronicDocumentList}
          columns={tableColumns}
          pagination={false}
          scroll={{ x: 1000 }}
          locale={{
            emptyText:
              checkEmpty === true ? (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={'Không có dữ liệu'}
                />
              ) : (
                <div style={{ opacity: 0.1, height: '150px' }} />
              ),
          }}
          loading={{
            tip: 'Đang tải văn bản...',
            spinning: loadingAnimationStore.tableLoading,
          }}
        />

        <div className="customPaginationWrapper">
          <div className="left-content-box">
            <span>Hiển thị</span>
            <Select
              value={queryStringParse.size ? parseInt(queryStringParse.size) : 10}
              onChange={value => {
                let params = { ...queryStringParse, size: value, page: 0 }
                history.push(`${location.pathname}?${queryString.stringify(params)}`)
              }}
              style={{ width: 110 }}
              className="custom-select">
              <Select.Option value={10}>10 / trang</Select.Option>
              <Select.Option value={20}>20 / trang</Select.Option>
              <Select.Option value={50}>50 / trang</Select.Option>
              <Select.Option value={100}>100 / trang</Select.Option>
            </Select>
            <span>Đi đến</span>
            <InputNumber
              min={1}
              max={Math.ceil(
                totalCount / (queryStringParse.size ? parseInt(queryStringParse.size) : 10)
              )}
              value={queryStringParse.page ? parseInt(queryStringParse.page) + 1 : 1}
              onPressEnter={e => {
                const value = e.target.value
                if (value) {
                  onChangePagination(
                    Number(value),
                    queryStringParse.size ? parseInt(queryStringParse.size) : 10
                  )
                }
              }}
              onBlur={e => {
                const value = e.target.value
                if (value) {
                  onChangePagination(
                    Number(value),
                    queryStringParse.size ? parseInt(queryStringParse.size) : 10
                  )
                }
              }}
            />
          </div>
          <Pagination
            onChange={onChangePagination}
            showSizeChanger={false}
            showLessItems
            current={
              queryStringParse.page ? parseInt(queryStringParse.page) + 1 : 1
            }
            pageSize={queryStringParse.size ? parseInt(queryStringParse.size) : 10}
            total={totalCount}
            hideOnSinglePage={true}
            className="customPagination"
          />
        </div>
      </div>

      {isVisiblePopupCreateSignDoc && (
        <PopupCreateSignDoc
          isVisiblePopupCreateSignDoc={isVisiblePopupCreateSignDoc}
          handleClosePopupCreateSignDoc={() =>
            setIsVisiblePopupCreateSignDoc(false)
          }
        />
      )}
    </DocumentsLayout>
  )
}

InternalDocumentSignDocPage.propTypes = {}

export default memo(
  withRouter(
    inject(
      'loadingAnimationStore',
      'electronicDocumentStore',
      'departmentStore',
      'listHistoryLogStore'
    )(observer(InternalDocumentSignDocPage))
  )
)
